// Client-side image compression.
//
// Re-encodes photos in the browser before upload so they land under a size
// budget (200 KB by default). Quality is protected by lowering resolution
// before quality: we binary-search the highest encoder quality that fits, and
// only when even the quality floor is too big do we step the dimensions down.
// WebP is preferred (smaller at equal quality, keeps transparency); browsers
// that cannot encode it fall back to JPEG.

export const IMAGE_SIZE_PRESETS = {
  // Covers, project photos, gallery, CMS media
  standard: { maxSizeKB: 200, maxDimension: 1920 },
  // Avatars, team photos, client logos, review photos
  small: { maxSizeKB: 50, maxDimension: 800 },
};

// Animated GIFs would lose their animation and SVGs are vector, so those pass through.
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];

const MAX_QUALITY = 0.92;
const MIN_QUALITY = 0.6;
const QUALITY_STEPS = 6;
const SCALE_STEP = 0.85;
const MIN_DIMENSION = 320;

let webpSupport;

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const supportsWebpEncoding = async () => {
  if (webpSupport === undefined) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const blob = await canvasToBlob(canvas, 'image/webp', 0.8);
    webpSupport = blob?.type === 'image/webp';
  }
  return webpSupport;
};

const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });

const renderToCanvas = (img, width, height, outputType) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // JPEG has no alpha channel; without a fill, transparent areas turn black
  if (outputType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

/** Highest-quality encoding of `canvas` that fits `maxBytes`, or the smallest one tried. */
const encodeWithinBudget = async (canvas, type, maxBytes) => {
  const best = await canvasToBlob(canvas, type, MAX_QUALITY);
  if (!best || best.size <= maxBytes) return { blob: best, fits: !!best };

  const floor = await canvasToBlob(canvas, type, MIN_QUALITY);
  if (!floor || floor.size > maxBytes) return { blob: floor, fits: false };

  let low = MIN_QUALITY;
  let high = MAX_QUALITY;
  let fitting = floor;
  for (let i = 0; i < QUALITY_STEPS; i += 1) {
    const mid = (low + high) / 2;
    // eslint-disable-next-line no-await-in-loop
    const blob = await canvasToBlob(canvas, type, mid);
    if (blob && blob.size <= maxBytes) {
      fitting = blob;
      low = mid;
    } else {
      high = mid;
    }
  }
  return { blob: fitting, fits: true };
};

const renameWithExtension = (name, type) => {
  const ext = type === 'image/webp' ? 'webp' : 'jpg';
  const base = (name || 'image').replace(/\.[^.]+$/, '') || 'image';
  return `${base}.${ext}`;
};

export const isCompressibleImage = (file) => !!file && COMPRESSIBLE_TYPES.includes(file.type);

/**
 * Compress an image File/Blob to fit within `maxSizeKB`.
 * Resolves to the original file when it is already small enough, not a
 * compressible type, cannot be decoded, or compression would not help.
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxSizeKB = IMAGE_SIZE_PRESETS.standard.maxSizeKB,
    maxDimension = IMAGE_SIZE_PRESETS.standard.maxDimension,
  } = options;
  const maxBytes = maxSizeKB * 1024;

  if (!isCompressibleImage(file)) return file;

  try {
    const img = await loadImage(file);
    const naturalMax = Math.max(img.naturalWidth, img.naturalHeight);
    // Re-encoding an image that already fits only adds generation loss
    if (file.size <= maxBytes && naturalMax <= maxDimension) return file;

    const outputType = (await supportsWebpEncoding()) ? 'image/webp' : 'image/jpeg';
    let scale = Math.min(1, maxDimension / naturalMax);
    let smallest = null;

    for (;;) {
      const width = Math.max(1, Math.round(img.naturalWidth * scale));
      const height = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = renderToCanvas(img, width, height, outputType);
      // eslint-disable-next-line no-await-in-loop
      const { blob, fits } = await encodeWithinBudget(canvas, outputType, maxBytes);
      if (blob && (!smallest || blob.size < smallest.size)) smallest = blob;
      if (fits || Math.max(width, height) <= MIN_DIMENSION) break;
      scale *= SCALE_STEP;
    }

    if (!smallest || smallest.size >= file.size) return file;

    return new File([smallest], renameWithExtension(file.name, smallest.type), {
      type: smallest.type,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Compress every base64 `data:` image embedded in rich-text HTML, keeping them
 * inline. Used where editor content is saved with its images embedded.
 */
export const compressInlineImages = async (html, options) => {
  if (!html || !html.includes('src="data:image')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img[src^="data:image"]'));
  const done = new Map();

  for (const img of images) {
    const src = img.getAttribute('src');
    if (!done.has(src)) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await (await fetch(src)).blob();
      // eslint-disable-next-line no-await-in-loop
      const compressed = await compressImage(blob, options);
      // eslint-disable-next-line no-await-in-loop
      done.set(src, compressed === blob ? src : await blobToDataUrl(compressed));
    }
    img.setAttribute('src', done.get(src));
  }

  return doc.body.innerHTML;
};
